import { Component, ElementRef, Inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { AppState } from '../../../app.state';
import { AuthenticationService } from '../../../core/services/authentication/authentication.service';
import { SharedService } from '../../../core/services/shared.service';
import { StorageService } from '../../../core/services/storage/storage.service';
import { Location } from '../../../shared/models/location/location.model';
import { NotificationType } from '../../../shared/models/notification-type';
import { ConfirmDialogComponent } from '../../../shared/services/dialog/confirm-dialog/confirm-dialog.component';
import { DialogService } from '../../../shared/services/dialog/dialog.service';
import { ErrorHandlerService } from '../../../shared/services/error-handler/error-handler.service';
import { NotificationService } from '../../../shared/services/notification/notification.service';
import { User } from '../../../shared/services/user/models/user.model';
import { UserService } from '../../../shared/services/user/user.service';
import { ValidationService } from '../../../shared/services/validation/validation.service';
import { AddEditLocationComponent } from '../../add-edit-location/add-edit-location.component';
import { ManageLocationsService } from '../../services/manage-locations.service';
import { ManageUsersService } from '../../services/manage-users.service';

@Component({
    selector: 'upsc-add-edit-user',
    templateUrl: './add-edit-user.component.html',
    styleUrls: ['./add-edit-user.component.scss'],
    // encapsulation: ViewEncapsulation.None,
})
export class AddEditUserComponent implements OnInit, OnDestroy {
    public userForm: UntypedFormGroup;
    public isAdministrator = false;
    public FirstName = '';
    public currentUserDefaultlocation: string;
    public currentUser: any;
    public currentUserCountryCode: string;
    public isPasswordChanged = false;
    public guestContacts = [];
    public isEUUser: boolean;
    public validationMessages = false;
    public isPasswordValid = false;
    public isConfirmPasswordValid = false;
    public isFRITUser: boolean;
    public shouldAllowPasswordUpdate = false;

    private ngDestroyed$ = new Subject<void>();

    @Input() editMode: boolean;
    @Input() user: User;
    @Input() locations: Location[];
    @Input() currentUserIsAdmin: boolean;
    @ViewChild('closeManageUser') closeManageUser: ElementRef;

    public constructor(private formBuilder: UntypedFormBuilder,
                       private manageUserService: ManageUsersService,
                       private dialog: MatDialog,
                       @Inject(MAT_DIALOG_DATA) public data: any,
                       private userData: User,
                       private selfdialogRef: MatDialogRef<AddEditUserComponent>,
                       private notificationService: NotificationService,
                       private errorHandlerService: ErrorHandlerService,
                       public confirmDialogRef: MatDialogRef<ConfirmDialogComponent>,
                       private manageLocationService: ManageLocationsService,
                       private translateservice: TranslateService,
                       private validationService: ValidationService,
                       private storageService: StorageService,
                       private dialogService: DialogService,
                       private userService: UserService,
                       private sharedService: SharedService,
                       private readonly appState: AppState,
                       private readonly translateService: TranslateService,
                       private readonly authenticationService: AuthenticationService,
    ) {
        this.userService.getCurrentUser().subscribe(
            (currentUser) => {
                this.currentUser = currentUser;
            },
        );

        this.user = data.user;
        this.editMode = data.editMode;
        this.locations = data.locations;
        this.currentUserIsAdmin = data.currentUserIsAdmin;
        this.guestContacts = this.locations.filter(x => x.ContactType === 3);

        this.CheckIfEUUser();
    }

    public ngOnInit(): void {
        if (!this.data.editMode) {// In Add Mode set current logged in user's default location
            this.user.DefaultLocation = this.data.defaultLocation.toLowerCase();
        }

        this.userForm = this.formBuilder.group({
            FirstName: [this.user.FirstName, Validators.required],
            LastName: [this.user.LastName, Validators.required],
            Email: [this.user.Email, Validators.compose([Validators.required, this.validationService.emailFormatValidator()])],
            //Password: [''],
            //ConfirmPassword: [{ value: '', disabled: true }],
            Password: [
                '', Validators.compose([
                    Validators.minLength(8),
                    this.validationService.containsAlphabet(),
                    this.validationService.containsNumber(),
                ]),
            ],
            ConfirmPassword: [{ value: '', disabled: true }],
            // UserId: [this.user.UserId, Validators.required],
            DefaultLocation: [this.user.DefaultLocation, Validators.required],
            IsAdmin: [!!this.user.IsAdmin],
            UserName: [this.user.UserName, Validators.required],
            IsAPIUser: [this.user.IsAPIUser ? this.user.IsAPIUser : false, Validators.required],
            IsGuest: [this.user.IsGuest ? this.user.IsGuest : false, Validators.required],
            IsGuestShipFrom: [this.user.IsGuestShipFrom ? this.user.IsGuestShipFrom.toString() : 'false'],
            IsGuestAddressBook: [this.user.IsGuestAddressBook ? this.user.IsGuestAddressBook.toString() : 'false'],
            GuestContactID: [this.user.GuestContactID ? this.user.GuestContactID : ''],
        }, {
            validators: this.validationService.isMatching('Password', 'ConfirmPassword'),
        });

        this.monitorValueChanges();
    }

    public ngOnDestroy(): void {
        this.ngDestroyed$.next();
        this.ngDestroyed$.complete();
    }

    getLocations() {
        this.manageLocationService.getLocations()
            .subscribe(
                (response) => {
                    if (response) {
                        this.locations = response;
                    }
                },
                (error) => {
                    this.notificationService.notify(
                        this.errorHandlerService.getHttpErrorMessage(error),
                        this.translateservice.instant('errorLoadingLocation'),
                        NotificationType.ERROR);
                });
    }

    saveUser() {
        this.fillObject();

        this.userData.Password = this.userForm.controls.Password.value;
        if (this.validateNewUser()) {
            const data = { ...this.userData };
            if (!this.shouldAllowPasswordUpdate) {
                delete data.Password;
            }

            this.manageUserService.addUser(data)
                .subscribe(
                    (response) => {
                        this.selfdialogRef.close({ result: 'success' });
                    },
                    (error) => {

                        this.notificationService.notify(
                            this.errorHandlerService.getHttpErrorMessage(error),
                            this.translateservice.instant('errorAddingUser'),
                            NotificationType.ERROR,
                        );
                    });
        }
    }

    updateUser() {
        this.fillObject();

        if (this.validateUser()) {
            const data = { ...this.userData };
            if (!this.shouldAllowPasswordUpdate) {
                delete data.Password;
            }

            this.manageUserService.updateUser(data).subscribe(
                (response) => {
                    if (this.userForm.controls['Password'].value) {// password changed so that need to be updated
                        this.changePassword();
                    }

                    this.selfdialogRef.close({ result: 'success' });
                },
                (error) => {
                    this.notificationService.notify(
                        this.errorHandlerService.getHttpErrorMessage(error),
                        'Error Updating User',
                        NotificationType.ERROR);
                });
        }
    }

    changePassword() {
        this.manageUserService.changePassword(this.userForm.controls['Password'].value, this.userData.UserId)
            .subscribe(
                (response) => {
                },
                (error) => {
                    this.notificationService.notify(
                        this.errorHandlerService.getHttpErrorMessage(error),
                        this.translateservice.instant('errorChangingPassword'),
                        NotificationType.ERROR);
                });
    }

    disableUser() {
        this.manageUserService.disableUser(this.user.UserId).subscribe(
            (response) => {
                this.selfdialogRef.close({ result: 'removed' });
            },
            (error) => {
                this.notificationService.notify(
                    this.errorHandlerService.getHttpErrorMessage(error),
                    this.translateservice.instant('errorRemovingUser'),
                    NotificationType.ERROR);
            });
    }

    removeConfirmation() {
        this.dialogService.confirm(
            this.translateService.instant('userRemoveConfirmation'),
            this.translateService.instant('WantToRemoveThisUser'))
            .subscribe(
                (isConfirmed) => {
                    if (!isConfirmed) {
                        return;
                    }

                    this.disableUser();
                });
    }

    validateNewUser(): boolean {
        for (const controlsKey in this.userForm.controls) {
            const control = this.userForm.controls[controlsKey];
            if (!control.valid && control.errors) {
                this.notificationService.notify(this.translateservice.instant('requireValid') + controlsKey, this.translateservice.instant('validationFailed'), NotificationType.ERROR);
                return false;
            }
        }

        if (this.shouldAllowPasswordUpdate) {
            // compare password
            return this.comparePasswords();
        }

        return true;
    }

    validateUser() {
        for (const controlsKey in this.userForm.controls) {
            if (controlsKey === 'Password' || controlsKey === 'ConfirmPassword') {
                if (this.userForm.controls['Password'].value &&
                    this.userForm.controls['Password'].value.trim() !== '' &&
                    !this.userForm.controls[controlsKey].valid) {
                    this.notificationService.notify(this.translateservice.instant('enterValidPassword'), this.translateservice.instant('validationFailed'), NotificationType.ERROR);
                    return false;
                }
                // compare password
                if (this.userForm.controls['Password'].value &&
                    this.userForm.controls['Password'].value.trim() !== '' &&
                    !this.comparePasswords()) {
                    return false;
                }
            } else if (!this.userForm.controls[controlsKey].valid) {
                this.notificationService.notify(this.translateservice.instant('requireValid') + controlsKey, this.translateservice.instant('validationFailed'), NotificationType.ERROR);
                return false;
            }
        }

        return true;
    }

    comparePasswords(): boolean {
        if (this.userForm.controls['Password'].value !== this.userForm.controls['ConfirmPassword'].value) {
            this.notificationService.notify(this.translateservice.instant('PasswordConfirmPasswordNotEqual'), this.translateservice.instant('validationFailed'), NotificationType.ERROR);
            return false;
        }
        return true;
    }

    fillObject() {
        this.userData.FirstName = this.userForm.controls.FirstName.value;
        this.userData.LastName = this.userForm.controls.LastName.value;
        this.userData.Email = this.userForm.controls.Email.value;
        this.userData.UserName = this.userForm.controls.UserName.value;
        this.userData.DefaultLocation = this.userForm.controls.DefaultLocation.value;
        this.userData.IsAdmin = this.userForm.controls.IsAdmin.value; // isAdministrator;
        this.userData.UserId = this.user.UserId;
        this.userData.IsAPIUser = this.userForm.controls.IsAPIUser.value;
        this.userData.IsGuest = this.userForm.controls.IsGuest.value;
        if (this.userData.IsGuest) {
            this.userData.IsGuestAddressBook = this.userForm.controls.IsGuestAddressBook.value;
            this.userData.IsGuestShipFrom = this.userForm.controls.IsGuestShipFrom.value;
            this.userData.GuestContactID = this.userForm.controls.GuestContactID.value;
        } else {
            this.userData.IsGuestAddressBook = false;
            this.userData.IsGuestShipFrom = false;
            this.userData.GuestContactID = '';
        }
    }

    public openEditLocation() {
        if (this.isEUUser && environment.feature.disableEditShipFromLocationEU) {
            const RestrictMessage = this.userService.GetRestrictShipFromAddressChangeMessage(this.currentUserCountryCode);
            this.dialogService.alert('', RestrictMessage);
            // this.dialogService.alert(
            //     '',
            //     this.translateService.instant('noShipFromEditAlert'),
            // );

            return;
        }

        const dialogConfig: MatDialogConfig = {
            disableClose: true,
            width: '600px',
            data: this.isFRITUser,
        };

        const dialogRef = this.dialog.open(AddEditLocationComponent, dialogConfig);

        dialogRef.afterClosed().subscribe((result) => {
            if (result && result.result === 'success') {
                this.getLocations();
            }
        });
    }

    public passwordFocus() {
        this.showHideValidationMessages(this.userForm.controls.Password.status);
    }

    public passwordFocusOut() {
        this.showHideValidationMessages(this.userForm.controls.Password.status);
    }

    public sendResetPasswordEmail(event: MouseEvent): void {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }

        this.authenticationService.passwordReset(
            this.userForm.controls.Email.value,
            this.userForm.controls.UserName.value,
        ).pipe(
            takeUntil(this.ngDestroyed$),
        ).subscribe(
            () => {
                this.notificationService.notify(
                    this.translateService.instant('UserResetPasswordEmailSent'),
                    'Email Sent',
                    NotificationType.SUCCESS,
                );
            },
        );
    }

    private monitorValueChanges() {
        this.userForm.controls.Password.valueChanges
            .pipe(distinctUntilChanged())
            .subscribe(
                (value) => {
                    const confirmPasswordControl = this.userForm.controls.ConfirmPassword;

                    if (!value.trim()) {
                        this.isPasswordChanged = false;
                        confirmPasswordControl.setValue('');
                        this.validationService.clearFormControlValidators([confirmPasswordControl]);
                        confirmPasswordControl.disable();
                        return;
                    }

                    this.isPasswordChanged = true;
                    this.validationService.setFormControlValidators(
                        confirmPasswordControl,
                        Validators.compose([Validators.required]));
                    confirmPasswordControl.enable();
                },
            );
        this.userForm.controls.IsGuest.valueChanges.subscribe(
            (value) => {
                const guestContactControl = this.userForm.controls.GuestContactID;
                if (value) {
                    this.validationService.setFormControlValidators(
                        guestContactControl,
                        Validators.compose([Validators.required]));
                } else {
                    this.validationService.clearFormControlValidators([guestContactControl]);
                }
            });
    }

    private CheckIfEUUser() {
        const user = this.appState.user$();
        this.currentUserCountryCode = user?.CountryCode;
        this.isEUUser = this.userService.isEUUser(user.CountryCode);
        this.isFRITUser = ['IT', 'FR'].includes(user?.CountryCode.toUpperCase());
    }

    private showHideValidationMessages(status) {
        if (status === 'VALID') {
            this.validationMessages = false;
            this.isPasswordValid = true;
        } else {
            this.validationMessages = true;
            this.isPasswordValid = false;
        }
    }
}
